import {
  Component,
  contentChildren,
  effect,
  EventEmitter,
  inject,
  input,
  linkedSignal,
  Output,
  viewChild,
  output,
} from '@angular/core';
import { IPageable, IPagination, IPaginationAlt } from 'src/app/shared/models';
import { MatSort, Sort } from '@angular/material/sort';
import { MatColumnDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';

import { PageableHelperService } from 'src/app/shared/services';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-table-wrapper',
  templateUrl: './table-wrapper.component.html',
  styleUrls: ['./table-wrapper.component.scss'],
  standalone: false,
})
export class TableWrapperComponent {
  private router = inject(Router);
  private location = inject(Location);
  private pageableHelper = inject(PageableHelperService);
  translate = inject(TranslateService);

  readonly tableConfig = input<IPagination<any> | IPaginationAlt<any>>(undefined);

  readonly sort = input<MatSort>(undefined);
  readonly filterPredicate = input<MatTableDataSource<any>['filterPredicate']>(undefined);
  readonly pageable = input<IPageable>(this.pageableHelper.getPageable());
  readonly pagebleViewModel = linkedSignal(() => this.pageable());
  readonly isPageable = input<boolean>(true);
  readonly applyZebraPattern = input(false);
  readonly pageableChange = output<IPageable>();

  readonly getData = output<void>();

  @Output() readonly rowClick = new EventEmitter<any>();

  readonly isUrlChangeable = input<boolean>(false);

  readonly table = viewChild(MatTable);

  public dataSource: MatTableDataSource<any>;
  public totalElements: number;
  public displayedColumns: string[] = [];
  private isInitialSetupDone: boolean = false;
  readonly columnDefs = contentChildren(MatColumnDef);

  constructor() {
    const translate = this.translate;

    translate.addLangs(['en']);
    translate.setDefaultLang('en');
    const browserLang = translate.getBrowserLang();
    translate.use(browserLang.match(/en/) ? browserLang : 'en');

    effect(() => {
      if (this.tableConfig() && this.columnDefs().length > 0) {
        this.loadDataTable(this.columnDefs());
      }
    });
  }

  public loadDataTable(columns: readonly MatColumnDef[]): void {
    if (this.isUrlChangeable()) {
      this.location.go(this.pageableHelper.getPageableUrl(this.pageable(), this.router));
    }

    this.totalElements = this.tableConfig().totalElements;
    this.displayedColumns = [];
    columns.forEach((column) => {
      this.displayedColumns.push(column.name);
      this.table().addColumnDef(column);
    });

    const tableConfig = this.tableConfig();
    this.dataSource = new MatTableDataSource<any>(tableConfig.items || tableConfig.content);
    const filterPredicate = this.filterPredicate();
    if (filterPredicate) {
      this.dataSource.filterPredicate = filterPredicate;
      this.dataSource.filter = 'update';
    }
    if (this.sort() && !this.isInitialSetupDone) {
      this.loadSort();
    }
  }

  private loadSort(): void {
    this.dataSource.sort = this.sort();
    this.dataSource.sort.sortChange.subscribe((sortChange: Sort) => {
      this.sortData(sortChange);
    });
    this.isInitialSetupDone = true;
  }

  public nextPage(event: PageEvent): void {
    this.pagebleViewModel.set({
      ...this.pageable(),
      page: event.pageIndex,
      size: event.pageSize,
    });
    this.pageableChange.emit(this.pagebleViewModel());
    this.getData.emit();
  }

  public sortData(sort: Sort): void {
    this.pagebleViewModel.set({
      ...this.pageable(),
      field: sort.active,
      order: sort.direction,
      page: 0,
    });
    this.pageableChange.emit(this.pagebleViewModel());
    this.getData.emit();
  }
}
